import { type FieldPath, get, useFormContext } from 'react-hook-form';

import type { PersonalInformation } from '@/features/onboarding/personal-information/schema';
import { useIsPeriodicReview } from '@/features/periodic-review/context';
import { useProfilePeriodicReviewIdentity } from '@/hooks/queries/use-profile-periodic-review';
import type { PeriodicReviewIdentity } from '@/services/api/profile/periodic-review';

const isLockedInPeriodicReview = (name: FieldPath<PersonalInformation>, locked: PeriodicReviewIdentity['locked'] | undefined) => {
  switch (name) {
    case 'countryOfBirth':
      return !!locked?.birthInfo?.country;
    case 'townOfBirth':
      return !!locked?.birthInfo?.locality;
    case 'dateOfBirth.day':
    case 'dateOfBirth.month':
    case 'dateOfBirth.year':
      return !!locked?.birthInfo?.date;
    default:
      return false;
  }
};

// Onboarding keeps an answer once it's given, whereas a periodic review can change any answer but those it locks
export const useIsPersonalInformationFieldLocked = () => {
  const form = useFormContext<PersonalInformation>();
  const isPeriodicReview = useIsPeriodicReview();
  const { data: draft } = useProfilePeriodicReviewIdentity(isPeriodicReview);
  const { defaultValues } = form.formState;

  return (name: FieldPath<PersonalInformation>) => (isPeriodicReview ? isLockedInPeriodicReview(name, draft?.locked) : !!get(defaultValues, name));
};
